/**
 * EditUserModal — Leadership-only edit dialog.
 *
 * Full name and role are editable; email is shown read-only. Save is disabled until the form is
 * dirty (a value differs from the loaded user). PUTs via useUpdateUser.
 *
 * Self-edit: when the target is the signed-in user, the Role field is disabled and the payload
 * never carries `role`. This mirrors the backend guard exactly — PUT /api/users/manage/{userId}
 * rejects a self role change with 403 `SELF_ROLE_CHANGE_FORBIDDEN` but still allows a self name
 * change. See packages/users/handlers/update-user.ts. The backend is the security boundary; this is
 * UX so the user is told the rule instead of discovering it via a failed save.
 */
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { useUpdateUser } from "@/hooks/useUsers";
import { apiErrorCode, apiErrorMessage } from "@/lib/api";
import type {
  DeliverProUser,
  DeliverProUserRole,
  UpdateUserInput,
} from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Tooltip } from "@/components/ui/tooltip";

const ROLES: DeliverProUserRole[] = ["PM", "SA", "Leadership"];

/** Shown on the disabled Role field and as its description when editing yourself. */
const SELF_ROLE_HINT =
  "You cannot change your own role. Ask another Leadership user to make this change.";

/** Fallback copy per backend code when the response carries no message. */
const UPDATE_ERROR_FALLBACKS: Record<string, string> = {
  SELF_ROLE_CHANGE_FORBIDDEN: SELF_ROLE_HINT,
  LAST_LEADERSHIP_USER:
    "This is the last active Leadership user. Promote another user to Leadership before changing this role.",
  ACTOR_NOT_LEADERSHIP:
    "Your Leadership role is no longer active. Refresh the page and try again.",
};

export function updateErrorMessage(err: unknown): string {
  const code = apiErrorCode(err);
  const fallback =
    (code && UPDATE_ERROR_FALLBACKS[code]) || "Failed to update user";
  return apiErrorMessage(err, fallback);
}

export function EditUserModal({
  user,
  open,
  onOpenChange,
  isSelf = false,
}: {
  user: DeliverProUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True when `user` is the signed-in user — locks the Role field. */
  isSelf?: boolean;
}): JSX.Element {
  const updateUser = useUpdateUser();

  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<DeliverProUserRole>(user.role);

  // Re-sync local state whenever a different user is opened.
  useEffect(() => {
    setFullName(user.full_name);
    setRole(user.role);
  }, [user]);

  const trimmedName = fullName.trim();
  const roleChanged = !isSelf && role !== user.role;
  const isDirty = trimmedName !== user.full_name || roleChanged;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!trimmedName) {
      toast.error("Full name is required");
      return;
    }
    const input: UpdateUserInput = {};
    if (trimmedName !== user.full_name) input.full_name = trimmedName;
    // `role` is omitted for self-edits so the request can never trip the backend guard.
    if (roleChanged) input.role = role;

    updateUser.mutate(
      { userId: user.id, input },
      {
        onSuccess: (result) => {
          // A role change revokes the target's sessions server-side; the new role only applies
          // once they sign in again, so say so instead of a bare "updated".
          const sessionRevoked = result?.session_revoked ?? roleChanged;
          if (result?.cognito_sync_failed) {
            // The change is saved and enforced (authorization reads the DB); only the Cognito
            // mirror lagged. Say so rather than claiming a sign-out that may not have happened.
            toast.warning(
              `${trimmedName} was updated and the change is in effect, but syncing it to the sign-in service failed. Their sign-in groups may lag until the next change.`,
            );
          } else if (sessionRevoked) {
            toast.success(
              `Role updated. ${trimmedName} will be signed out and must sign in again for the new role to apply.`,
            );
          } else {
            toast.success("User updated");
          }
          onOpenChange(false);
        },
        onError: (err) => {
          // Show the server's explanation (e.g. SELF_ROLE_CHANGE_FORBIDDEN, LAST_LEADERSHIP_USER)
          // — not axios' generic "Request failed with status code 403".
          toast.error(updateErrorMessage(err));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            {isSelf
              ? "Update your name. Your own role cannot be changed here."
              : "Update the user's name and role."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-user-email">Email</Label>
            <Input id="edit-user-email" value={user.email} readOnly disabled />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-user-name">Full Name</Label>
            <Input
              id="edit-user-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-user-role">Role</Label>
            <Tooltip text={isSelf ? SELF_ROLE_HINT : null}>
              <div className="w-full">
                <Select
                  id="edit-user-role"
                  aria-label="Role"
                  value={role}
                  onChange={(v) => setRole(v as DeliverProUserRole)}
                  options={ROLES.map((r) => ({ value: r, label: r }))}
                  disabled={isSelf}
                  aria-describedby={isSelf ? "edit-user-role-hint" : undefined}
                />
              </div>
            </Tooltip>
            {isSelf && (
              <p
                id="edit-user-role-hint"
                className="text-xs text-text-secondary"
              >
                {SELF_ROLE_HINT}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isDirty || updateUser.isPending}>
              {updateUser.isPending && (
                <Loader2 className="animate-spin" aria-hidden="true" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditUserModal;
